import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { sendRemoveRequest } from '../services/removeServices';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import styled from 'styled-components';

const NOSPAM_THIRD_LETTER = 'S'; // third letter of NOSPAM

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 1rem 0;
`;

const Intro = styled.p`
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 0.75rem 0;
  line-height: 1.6;
`;

const DmcaBox = styled.div`
  margin: 1.5rem 0 2rem 0;
  padding: 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.6;
`;

const DmcaTitle = styled.strong`
  display: block;
  color: ${({ theme }) => theme.colors.foreground};
  margin-bottom: 0.5rem;
`;

const Form = styled.form`
  max-width: 42rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  display: block;
  margin-bottom: 0.5rem;
`;

const Hint = styled.span`
  display: block;
  font-size: 0.8125rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-top: 0.25rem;
`;

const Input = styled.input`
  width: 100%;
  height: 3rem;
  padding: 0 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  box-sizing: border-box;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const RadioWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.foreground};
  cursor: pointer;
`;

const SubmitBtn = styled.button`
  align-self: flex-start;
  height: 3rem;
  padding: 0 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessCard = styled(motion.div)`
  max-width: 42rem;
  margin-top: 1rem;
  padding: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(34, 197, 94, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: hsl(142, 71%, 45%);
`;

const SuccessTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 1rem 0 0 0;
`;

const SuccessDesc = styled.p`
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

const ContactLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

function Remove() {
  const { prependPath } = useLocale();
  const t = useT();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    domainUrl: '',
    reason: '',
    removeOption: 'all',
    nospam: '',
  });

  const nospamValid = form.nospam.trim().toUpperCase() === NOSPAM_THIRD_LETTER;
  const canSubmit = form.name && form.email && form.domainUrl && form.reason && nospamValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      await sendRemoveRequest(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit removal request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <SeoHead title="Remove a site | CiteMatch" description="Request removal of your domain from CiteMatch." noindex />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={prependPath('/')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>
        <Title>Remove your domain/website from our database</Title>
        <Intro>
          If you want to remove your website from our database and website, please fill out the following form.
        </Intro>
        <Intro>
          You must use an email from the same domain — not Gmail, Yahoo, or another provider. This is for proof that the website/domain owner is sending the request.
        </Intro>
        <Intro>
          You can also <ContactLink to={prependPath('/contact')}>contact us</ContactLink> if you need further information.
        </Intro>

        <DmcaBox>
          <DmcaTitle>DMCA — For owners of copyright</DmcaTitle>
          This site does not store any illegal files on its servers. We only index and link to other sites, similar to search engines like Google. Most of the sites are updated by the website owners, by our visitors, or by our team.
        </DmcaBox>

        {submitted ? (
          <SuccessCard initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <SuccessIcon>
              <CheckCircle size={32} />
            </SuccessIcon>
            <SuccessTitle>Request received</SuccessTitle>
            <SuccessDesc>We will verify your request and process the removal. You will be contacted at the email you provided if we need any clarification.</SuccessDesc>
          </SuccessCard>
        ) : (
          <Form onSubmit={handleSubmit}>
            {error && <p style={{ color: 'hsl(0, 84%, 60%)', margin: '0 0 0.25rem 0' }}>{error}</p>}
            <div>
              <Label>Name</Label>
              <Input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Name"
              />
            </div>
            <div>
              <Label>Email address</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@yourdomain.com"
              />
              <Hint>Only email from same domain. We'll never share your email with anyone else.</Hint>
            </div>
            <div>
              <Label>Domain URL</Label>
              <Input
                type="url"
                required
                value={form.domainUrl}
                onChange={(e) => setForm({ ...form, domainUrl: e.target.value })}
                placeholder="https://yourdomain.com"
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea
                required
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Please provide as much detail as possible."
              />
            </div>
            <div>
              <Label>Remove options</Label>
              <RadioWrap>
                <input
                  type="radio"
                  id="remove-all"
                  name="removeOption"
                  value="all"
                  checked={form.removeOption === 'all'}
                  onChange={(e) => setForm({ ...form, removeOption: e.target.value })}
                />
                <RadioLabel htmlFor="remove-all">All — completely remove</RadioLabel>
              </RadioWrap>
            </div>
            <div>
              <Label>What is the third letter of the word NOSPAM?</Label>
              <Input
                type="text"
                required
                maxLength={1}
                value={form.nospam}
                onChange={(e) => setForm({ ...form, nospam: e.target.value })}
                placeholder="?"
                style={{ maxWidth: '4rem' }}
              />
              {form.nospam && !nospamValid && (
                <Hint style={{ color: 'hsl(0, 84%, 60%)' }}>Incorrect. Enter the third letter only.</Hint>
              )}
            </div>
            <SubmitBtn type="submit" disabled={!canSubmit || loading}>
              <Send size={16} strokeWidth={2.5} /> Submit removal request
            </SubmitBtn>
          </Form>
        )}
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default Remove;
